/**
 * Task Panel Component
 * Collapsible panel with three states: collapsed (40px), partial (280px), full (500px)
 */

import React, { useState, useEffect } from "react";
import { Button, Typography, Badge, Spin } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  ExpandOutlined,
  CompressOutlined,
  CheckSquareOutlined,
} from "@ant-design/icons";
import { useList } from "@refinedev/core";
import { TaskCard, TaskCardData } from "./TaskCard";
import { TaskBoard, ProjectRow } from "./TaskBoard";
import { TaskQuickAdd } from "./TaskQuickAdd";
import { ContextTags } from "./ContextTags";

const { Text, Title } = Typography;

export type PanelState = "collapsed" | "partial" | "full";

export interface TaskPanelProps {
  defaultState?: PanelState;
}

// Panel widths
const PANEL_WIDTHS: Record<PanelState, number | string> = {
  collapsed: 40,
  partial: 280,
  full: "66vw", // 2/3 of screen
};

export const TaskPanel: React.FC<TaskPanelProps> = ({
  defaultState = "collapsed",
}) => {
  const [panelState, setPanelState] = useState<PanelState>(defaultState);
  const [selectedContexts, setSelectedContexts] = useState<string[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch active projects
  const { data: projectsData, isLoading: projectsLoading } = useList({
    resource: "projects",
    pagination: { pageSize: 50 },
    filters: [
      {
        field: "conditions",
        operator: "eq",
        value: "projectstatus != 'archived'",
      },
    ],
    queryOptions: {
      refetchInterval: 30000, // Refresh every 30 seconds
    },
  });

  // Fetch all tasks
  const {
    data: tasksData,
    isLoading: tasksLoading,
    refetch: refetchTasks,
  } = useList({
    resource: "project-tasks",
    pagination: { pageSize: 200 },
    filters: [
      {
        field: "conditions",
        operator: "eq",
        value: "projecttaskstatus != 'Completed'",
      },
    ],
    queryOptions: {
      refetchInterval: 30000,
    },
  });

  // Refresh tasks when refreshKey changes
  useEffect(() => {
    if (refreshKey > 0) {
      refetchTasks();
    }
  }, [refreshKey, refetchTasks]);

  const projects = projectsData?.data || [];
  const tasks = tasksData?.data || [];
  const isLoading = projectsLoading || tasksLoading;

  // Transform data for TaskBoard
  const projectRows: ProjectRow[] = projects.map((project: any) => {
    const projectTasks = tasks
      .filter((t: any) => t.projectid === project.id)
      .map(
        (t: any): TaskCardData => ({
          id: t.id,
          name: t.projecttaskname,
          projectId: project.id,
          projectName: project.projectname,
          priority: t.projecttaskpriority || "someday",
          status: t.projecttaskstatus || "Open",
          context: t.context,
          dueDate: t.enddate,
        })
      );

    const isActive = !["completed", "archived", "delivered"].includes(
      project.projectstatus?.toLowerCase()
    );

    return {
      id: project.id,
      name: project.projectname,
      isActive,
      tasks: projectTasks,
    };
  });

  // Sort: active projects first
  projectRows.sort((a, b) => {
    if (a.isActive && !b.isActive) return -1;
    if (!a.isActive && b.isActive) return 1;
    return 0;
  });

  // Get NOW and NEXT tasks for partial view
  const nowTasks = tasks
    .filter((t: any) => t.projecttaskpriority === "now")
    .map((t: any): TaskCardData => {
      const project = projects.find((p: any) => p.id === t.projectid);
      return {
        id: t.id,
        name: t.projecttaskname,
        projectId: t.projectid,
        projectName: project?.projectname || "Unknown",
        priority: "now",
        status: t.projecttaskstatus || "Open",
        context: t.context,
      };
    });

  const nextTasks = tasks
    .filter((t: any) => t.projecttaskpriority === "tomorrow")
    .slice(0, 3) // Show only first 3 next tasks
    .map((t: any): TaskCardData => {
      const project = projects.find((p: any) => p.id === t.projectid);
      return {
        id: t.id,
        name: t.projecttaskname,
        projectId: t.projectid,
        projectName: project?.projectname || "Unknown",
        priority: "tomorrow",
        status: t.projecttaskstatus || "Open",
        context: t.context,
      };
    });

  const handleContextToggle = (context: string) => {
    setSelectedContexts((prev) =>
      prev.includes(context)
        ? prev.filter((c) => c !== context)
        : [...prev, context]
    );
  };

  const handleTaskCreated = () => {
    setRefreshKey((k) => k + 1);
  };

  const handleTaskUpdated = () => {
    // Trigger refresh after task update
    refetchTasks();
  };

  // Collapsed state - just arrow
  if (panelState === "collapsed") {
    return (
      <div
        style={{
          width: PANEL_WIDTHS.collapsed,
          height: "100%",
          background: "#fffbf5",
          borderLeft: "1px solid #f1f5f9",
          boxShadow: "-1px 0 3px rgba(0, 0, 0, 0.02)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <Button
          type="text"
          icon={<LeftOutlined />}
          onClick={() => setPanelState("partial")}
          style={{
            color: "#64748b",
            position: "absolute",
            top: 8,
            left: 8,
          }}
        />
        <div
          style={{
            writingMode: "vertical-rl",
            textOrientation: "mixed",
            marginTop: 48,
            color: "#64748b",
            fontWeight: 600,
            fontSize: 11,
            letterSpacing: "0.5px",
          }}
        >
          <CheckSquareOutlined style={{ marginBottom: 8 }} />
          TASKS
        </div>
        <Badge
          count={nowTasks.length}
          style={{ backgroundColor: "#ef4444", marginTop: 8 }}
        />
      </div>
    );
  }

  // Partial state - list view
  if (panelState === "partial") {
    return (
      <div
        style={{
          width: PANEL_WIDTHS.partial,
          height: "100%",
          background: "#fff",
          borderLeft: "1px solid #f1f5f9",
          boxShadow: "-1px 0 3px rgba(0, 0, 0, 0.02)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "14px 16px",
            background: "#fffbf5",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Title
            level={5}
            style={{ margin: 0, color: "#3d4f5f", fontSize: 15 }}
          >
            <CheckSquareOutlined style={{ marginRight: 8 }} /> Tasks
          </Title>
          <div>
            <Button
              type="text"
              size="small"
              icon={<ExpandOutlined />}
              onClick={() => setPanelState("full")}
            />
            <Button
              type="text"
              size="small"
              icon={<RightOutlined />}
              onClick={() => setPanelState("collapsed")}
            />
          </div>
        </div>

        {/* Quick Add */}
        <div
          style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0" }}
        >
          <TaskQuickAdd onTaskCreated={handleTaskCreated} />
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: "auto", padding: "12px 16px" }}>
          {isLoading ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              <Spin />
            </div>
          ) : (
            <>
              {/* NOW Tasks */}
              <div style={{ marginBottom: 16 }}>
                <Text
                  strong
                  style={{
                    color: "#ef4444",
                    fontSize: 11,
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                  }}
                >
                  Now ({nowTasks.length})
                </Text>
                <div style={{ marginTop: 8 }}>
                  {nowTasks.length === 0 ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Nessun task urgente
                    </Text>
                  ) : (
                    nowTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        compact
                        onUpdate={handleTaskUpdated}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* NEXT Tasks */}
              <div>
                <Text
                  strong
                  style={{
                    color: "#f59e0b",
                    fontSize: 11,
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                  }}
                >
                  Next ({nextTasks.length})
                </Text>
                <div style={{ marginTop: 8 }}>
                  {nextTasks.length === 0 ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Nessun task in coda
                    </Text>
                  ) : (
                    nextTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        compact
                        onUpdate={handleTaskUpdated}
                      />
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "8px 16px",
            borderTop: "1px solid #f0f0f0",
            textAlign: "center",
          }}
        >
          <Button
            type="link"
            size="small"
            onClick={() => setPanelState("full")}
          >
            Mostra tutto →
          </Button>
        </div>
      </div>
    );
  }

  // Full state - timeline board
  return (
    <div
      style={{
        width: PANEL_WIDTHS.full,
        height: "100%",
        background: "#fff",
        borderLeft: "1px solid #f1f5f9",
        boxShadow: "-2px 0 6px rgba(0, 0, 0, 0.03)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "14px 20px",
          background: "#fffbf5",
          borderBottom: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Title level={5} style={{ margin: 0, color: "#3d4f5f", fontSize: 15 }}>
          Task Board
        </Title>
        <div>
          <Button
            type="text"
            size="small"
            icon={<CompressOutlined />}
            onClick={() => setPanelState("partial")}
          />
          <Button
            type="text"
            size="small"
            icon={<RightOutlined />}
            onClick={() => setPanelState("collapsed")}
          />
        </div>
      </div>

      {/* Context Filters */}
      <div style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0" }}>
        <ContextTags
          selectedContexts={selectedContexts}
          onContextToggle={handleContextToggle}
        />
      </div>

      {/* Quick Add */}
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0" }}>
        <TaskQuickAdd onTaskCreated={handleTaskCreated} />
      </div>

      {/* Task Board */}
      <div style={{ flex: 1, overflow: "auto", padding: "12px 16px" }}>
        <TaskBoard
          projects={projectRows}
          isLoading={isLoading}
          selectedContexts={selectedContexts}
          onTaskUpdate={handleTaskUpdated}
        />
      </div>
    </div>
  );
};
